import type { GroupBase, StylesConfig } from 'react-select';

import { getRgbaValue } from '../../tokens/colors';
import { rem } from '../../tokens/spacing';
import { zIndex } from '../../tokens/zIndex';
import type { Theme } from '../../theme/types';

export interface CreateWealthlaneReactSelectStylesOptions {
  hasError?: boolean;
  /** Optional fixed width for the control (legacy `width` prop); default full width. */
  controlWidth?: string;
}

function cv(theme: Theme, key: string, fallback: string): string {
  return theme.cssVars[key] ?? fallback;
}

/**
 * `react-select` styles aligned with legacy `formSelect.tsx`, driven by {@link Theme} tokens
 * so light / dark / high-contrast and brand colors track `ThemeProvider`.
 */
export function createWealthlaneReactSelectStyles<Option, IsMulti extends boolean>(
  theme: Theme,
  opts: CreateWealthlaneReactSelectStylesOptions = {},
): StylesConfig<Option, IsMulti, GroupBase<Option>> {
  const surface = cv(theme, '--ui-surface', '#ffffff');
  const text = cv(theme, '--ui-text', '#161616');
  const textSubtle = cv(theme, '--ui-text-subtle', '#767676');
  const textMuted = cv(theme, '--ui-text-muted', '#595959');
  const border = cv(theme, '--ui-input-border', cv(theme, '--ui-border', '#d9d9d9'));
  const borderFocus = cv(theme, '--ui-input-border-focus', '#1a73e8');
  const interactive = cv(theme, '--ui-interactive', '#6b4d9a');
  /** Focused option label color — matches legacy `formSelect` (blue accent). */
  const optionFocusColor = cv(theme, '--ui-input-border-focus', interactive);
  const danger = cv(theme, '--ui-status-danger', '#d32f2f');
  const shadow = cv(theme, '--ui-input-shadow', 'rgba(0,0,0,0.12)');
  const inset = cv(theme, '--ui-background-muted', '#f7f7f7');
  const focusOptionBg = getRgbaValue('#f3f3f3', 0.85);

  /** Same elevation as TextInput — `filter: drop-shadow` for parity across controls. */
  const controlShadow = `drop-shadow(${rem(0)} ${rem(2)} ${rem(4)} ${shadow})`;
  const menuShadow = `0 ${rem(2)} ${rem(4)} ${shadow}`;

  /**
   * Height contract for ALL react-select consumers (Select, RhfSelect, RhfAsyncSelect
   * — single AND multi/chips). Must match TextInput / NumberInput / CheckboxSelect:
   *   padding 11+11 + line-height 16 + border 1+1 = 40px
   * Inner content area is 38px (40 border-box minus 1px borders).
   * Multi only grows above 40px when chips wrap to a second row.
   */
  return {
    control: (styles, state) => ({
      ...styles,
      borderRadius: `var(--ui-radius-md, ${rem(6)})`,
      fontWeight: 400,
      fontSize: rem(15),
      lineHeight: rem(16),
      minHeight: rem(40),
      // Empty / single-select: hard lock 40. Multi with values: auto so wrapped chips can grow.
      height: state.isMulti && state.hasValue ? 'auto' : rem(40),
      transition: 'all 0.2s linear',
      border:
        opts.hasError === true
          ? `${rem(1)} solid ${danger} !important`
          : state.isFocused
            ? `${rem(1)} solid ${borderFocus} !important`
            : `${rem(1)} solid ${border} !important`,
      boxShadow: 'none',
      filter: controlShadow,
      width: opts.controlWidth ?? '100%',
      maxWidth: '100%',
      minWidth: 0,
      // Disabled: use muted background to match TextInput/DatePicker disabled appearance
      backgroundColor: state.isDisabled ? inset : surface,
      color: text,
      cursor: state.isDisabled ? 'not-allowed' : 'pointer',
      boxSizing: 'border-box',
      alignItems: state.isMulti && state.hasValue ? 'flex-start' : 'center',
      ':hover': {
        border: state.isDisabled ? `${rem(1)} solid ${border} !important` : `${rem(1)} solid ${borderFocus} !important`,
      },
    }),
    valueContainer: (styles, state) => {
      const isMultiWithValues = state.isMulti && state.hasValue;
      const verticalPad = isMultiWithValues ? rem(4) : 0;
      return {
        ...styles,
        minHeight: rem(38),
        // Single-select + empty multi: lock 38. Multi with values: auto for chip wrap.
        height: isMultiWithValues ? 'auto' : rem(38),
        paddingTop: verticalPad,
        paddingBottom: verticalPad,
        paddingLeft: rem(16),
        paddingRight: rem(5),
        gap: rem(4),
        rowGap: rem(4),
        alignItems: 'center',
        flexWrap: 'wrap',
      };
    },
    singleValue: (styles) => ({
      ...styles,
      color: text,
      lineHeight: rem(16),
      margin: 0,
    }),
    placeholder: (styles) => ({
      ...styles,
      padding: 0,
      margin: 0,
      color: textSubtle,
      lineHeight: rem(16),
      whiteSpace: 'nowrap',
    }),
    input: (styles) => ({
      ...styles,
      color: text,
      // react-select defaults: margin 2px + paddingTop/Bottom 2px — these blow past 40px.
      margin: 0,
      paddingTop: 0,
      paddingBottom: 0,
      lineHeight: rem(16),
    }),
    menu: (styles) => ({
      ...styles,
      background: 'transparent',
      boxShadow: 'none',
      zIndex: zIndex.floatingPortal,
    }),
    menuPortal: (styles) => ({
      ...styles,
      zIndex: zIndex.floatingPortal,
    }),
    menuList: (styles) => ({
      ...styles,
      maxHeight: rem(256),
      fontSize: rem(14),
      padding: rem(10),
      borderRadius: `var(--ui-radius-sm, ${rem(4)})`,
      width: 'fit-content',
      minWidth: '100%',
      background: surface,
      border: `${rem(1)} solid ${border} !important`,
      boxShadow: menuShadow,
      '::-webkit-scrollbar': {
        width: rem(5),
      },
      '::-webkit-scrollbar-track': {
        background: inset,
        borderRadius: `var(--ui-radius-md, ${rem(7)})`,
      },
      '::-webkit-scrollbar-thumb': {
        background: textMuted,
        borderRadius: `var(--ui-radius-md, ${rem(7)})`,
      },
      '::-webkit-scrollbar-thumb:hover': {
        background: text,
      },
    }),
    indicatorsContainer: (styles, state) => ({
      ...styles,
      height: state.isMulti && state.hasValue ? 'auto' : rem(38),
      minHeight: rem(38),
      alignSelf: state.isMulti && state.hasValue ? 'flex-start' : 'stretch',
      alignItems: 'center',
      paddingTop: state.isMulti && state.hasValue ? rem(4) : 0,
    }),
    clearIndicator: (styles) => ({
      ...styles,
      padding: `${rem(8)} ${rem(4)}`,
      display: 'flex',
      alignItems: 'center',
    }),
    dropdownIndicator: (styles) => ({
      ...styles,
      padding: `${rem(8)} ${rem(8)}`,
      display: 'flex',
      alignItems: 'center',
    }),
    indicatorSeparator: (styles) => ({
      ...styles,
      display: 'none',
    }),
    option: (styles, state) => ({
      ...styles,
      backgroundColor: state.isFocused ? focusOptionBg : undefined,
      color: state.isDisabled ? textSubtle : state.isFocused ? optionFocusColor : text,
      fontWeight: state.isFocused && !state.isDisabled ? 500 : 400,
      borderRadius: `var(--ui-radius-sm, ${rem(4)})`,
      wordBreak: 'break-all',
      whiteSpace: 'normal',
      maxWidth: rem(600),
      cursor: state.isDisabled ? 'not-allowed' : 'pointer',
    }),
    group: (styles) => ({
      ...styles,
      paddingTop: rem(4),
    }),
    groupHeading: (styles) => ({
      ...styles,
      fontSize: rem(12),
      fontWeight: 600,
      color: textMuted,
      padding: `${rem(6)} ${rem(12)}`,
      margin: 0,
      textTransform: 'none',
    }),
    // Chip sizing must fit a single-row multi-select inside the 40px control.
    // Old padding 6 + lineHeight 20 alone was already 32px and forced the control taller.
    multiValue: (styles) => ({
      ...styles,
      margin: 0,
      borderRadius: `var(--ui-radius-sm, ${rem(4)})`,
      alignItems: 'center',
      height: rem(24),
      minHeight: rem(24),
    }),
    multiValueLabel: (styles) => ({
      ...styles,
      fontSize: rem(14),
      lineHeight: rem(16),
      padding: `${rem(2)} ${rem(6)}`,
      whiteSpace: 'nowrap',
    }),
    multiValueRemove: (styles) => ({
      ...styles,
      paddingLeft: rem(2),
      paddingRight: rem(4),
      ':hover': {
        backgroundColor: 'transparent',
        color: danger,
      },
    }),
    loadingIndicator: (styles) => ({
      ...styles,
      color: interactive,
    }),
    noOptionsMessage: (styles) => ({
      ...styles,
      color: textMuted,
      fontSize: rem(14),
    }),
    loadingMessage: (styles) => ({
      ...styles,
      color: textMuted,
      fontSize: rem(14),
    }),
  };
}
